import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon, ArrowLeft, Sparkles, Check, X, Users, Lock, Coins } from "lucide-react";
import { toast } from "sonner";

const DIFFICULTIES = [
  { value: "easy", label: "Easy", credits: 100 },
  { value: "medium", label: "Medium", credits: 300 },
  { value: "hard", label: "Hard", credits: 600 },
  { value: "expert", label: "Expert", credits: 1000 },
];

export default function NewRequest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [budget, setBudget] = useState(null);
  const [peoplePickerOpen, setPeoplePickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "", tags: "",
    difficulty: "medium", bounty_credits: 300, visibility: "org",
    department: user?.department || "", due_date: null, allowed_user_ids: [],
  });

  useEffect(() => {
    api.get("/org/categories").then((r) => setCategories(r.data));
    api.get("/org/departments").then((r) => setDepts(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.department && user?.department) {
      setForm((f) => ({ ...f, department: user.department }));
    }
  }, [user?.department]);

  useEffect(() => {
    const dept = form.department || user?.department;
    if (!dept) { setBudget(null); return; }
    api.get("/department-budgets/me")
      .then((r) => {
        // If form department differs from user's, fetch all and pick.
        if (r.data.department === dept) setBudget(r.data);
        else {
          api.get("/department-budgets").then((all) => {
            setBudget(all.data.find((b) => b.department === dept) || null);
          }).catch(() => setBudget(null));
        }
      })
      .catch(() => {
        api.get("/department-budgets").then((all) => {
          setBudget(all.data.find((b) => b.department === dept) || null);
        }).catch(() => setBudget(null));
      });
  }, [form.department, user?.department]);

  const toggleUser = (id) => {
    setForm((f) => {
      const has = f.allowed_user_ids.includes(id);
      return {
        ...f,
        allowed_user_ids: has
          ? f.allowed_user_ids.filter((x) => x !== id)
          : [...f.allowed_user_ids, id],
      };
    });
  };

  const bounty = Number(form.bounty_credits) || 0;
  const maxPerTicket = budget?.max_credits_per_ticket ?? null;
  const remaining = budget?.remaining_credits ?? null;
  const isOrgOwner = user?.role === "owner";
  // Owners have god-level power — never gated by dept max / approval.
  const needsApproval = !isOrgOwner && maxPerTicket != null && bounty > maxPerTicket;
  // Owners can't claim/solve; creator is always included server-side — don't list either.
  const selectableUsers = users.filter(
    (u) => u.role !== "owner" && u.id !== user?.id,
  );
  const selectedPeopleIds = form.allowed_user_ids.filter((id) => selectableUsers.some((u) => u.id === id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.department) {
      toast.error("Select a department so the bounty can be charged to its budget.");
      return;
    }
    if (form.visibility === "specific" && selectedPeopleIds.length === 0) {
      toast.error("Select at least one person, or make the request open to everyone.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        due_date: form.due_date ? form.due_date.toISOString() : null,
        bounty_credits: Number(form.bounty_credits),
        allowed_user_ids: form.visibility === "specific" ? selectedPeopleIds : [],
      };
      const { data } = await api.post("/requests", payload);
      if (data.status === "pending_approval") {
        toast.success("Submitted for department admin approval");
      } else {
        toast.success("Request published");
      }
      navigate(`/app/requests/${data.id}`);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error(msg.includes("verify") ? `${msg} Use the banner above to resend.` : msg);
    } finally {
      setLoading(false);
    }
  };

  const setDifficulty = (v) => {
    const d = DIFFICULTIES.find((x) => x.value === v);
    setForm({ ...form, difficulty: v, bounty_credits: d?.credits ?? form.bounty_credits });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-600">New Request</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">What do you need?</h1>
        <p className="text-slate-600 mt-1">Write it like you'd ask a smart colleague over coffee. Be specific.</p>
      </div>

      {budget && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap gap-4 items-center" data-testid="budget-banner">
          <Coins className="h-5 w-5 text-amber-500" />
          <div className="text-sm">
            <span className="font-semibold">{budget.department}</span>
            <span className="text-slate-500"> · </span>
            <span className="font-mono">{budget.remaining_credits}</span> credits remaining
            <span className="text-slate-500"> · max </span>
            <span className="font-mono">{budget.max_credits_per_ticket}</span> per ticket
          </div>
          {needsApproval && (
            <Badge variant="outline" className="ml-auto text-orange-700 border-orange-300 bg-orange-50">
              Needs dept admin approval
            </Badge>
          )}
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-8">
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider">Title</Label>
              <Input
                required
                value={form.title}
                data-testid="new-title"
                placeholder="Need intro to a CTO at a Series B fintech in India"
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="h-12 text-base"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider">Description</Label>
              <Textarea
                required
                rows={5}
                value={form.description}
                data-testid="new-description"
                placeholder="Context, why you're asking, what good looks like…"
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="new-category" required><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Department</Label>
                {user?.role === "owner" ? (
                  <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                    <SelectTrigger data-testid="new-department"><SelectValue placeholder="Required" /></SelectTrigger>
                    <SelectContent>
                      {depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.department || user?.department || ""}
                    disabled
                    data-testid="new-department"
                    className="bg-slate-50"
                  />
                )}
                {user?.role !== "owner" && (
                  <p className="text-xs text-slate-500">Tickets are charged to your home department budget.</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider">Tags (comma separated)</Label>
              <Input
                value={form.tags}
                data-testid="new-tags"
                placeholder="fintech, cto, networking"
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Difficulty</Label>
                <Select value={form.difficulty} onValueChange={setDifficulty}>
                  <SelectTrigger data-testid="new-difficulty"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIFFICULTIES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Bounty (credits)</Label>
                <Input
                  type="number" min="0" required
                  value={form.bounty_credits}
                  data-testid="new-bounty"
                  onChange={(e) => setForm({ ...form, bounty_credits: e.target.value })}
                />
                {remaining != null && bounty > remaining && !needsApproval && !isOrgOwner && (
                  <p className="text-xs text-red-600">Exceeds remaining department budget ({remaining}c).</p>
                )}
                {isOrgOwner && remaining != null && bounty > remaining && (
                  <p className="text-xs text-slate-500">Owner publish will auto-top-up this department budget.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Due date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" data-testid="new-duedate" className="w-full justify-start font-normal">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {form.due_date ? format(form.due_date, "PPP") : "Optional"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={form.due_date}
                      onSelect={(d) => setForm({ ...form, due_date: d })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-xs font-medium uppercase tracking-wider">Who can see this?</Label>
              <RadioGroup
                value={form.visibility === "specific" ? "specific" : "org"}
                onValueChange={(v) => setForm({ ...form, visibility: v })}
                className="grid grid-cols-2 gap-3"
              >
                <label
                  htmlFor="vis-org"
                  className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${form.visibility !== "specific" ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <RadioGroupItem value="org" id="vis-org" className="mt-0.5" data-testid="visibility-org" />
                  <div>
                    <div className="flex items-center gap-1.5 font-medium text-sm"><Users className="h-4 w-4" /> Open to everyone</div>
                    <p className="text-xs text-slate-500 mt-0.5">All members of your organization can see and claim this request.</p>
                  </div>
                </label>
                <label
                  htmlFor="vis-specific"
                  className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${form.visibility === "specific" ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <RadioGroupItem value="specific" id="vis-specific" className="mt-0.5" data-testid="visibility-specific" />
                  <div>
                    <div className="flex items-center gap-1.5 font-medium text-sm"><Lock className="h-4 w-4" /> Specific people</div>
                    <p className="text-xs text-slate-500 mt-0.5">Only the people you pick (plus you) can see and claim this. Owners always see it but aren’t listed — they can’t claim or solve.</p>
                  </div>
                </label>
              </RadioGroup>

              {form.visibility === "specific" && (
                <div className="space-y-2">
                  <Popover open={peoplePickerOpen} onOpenChange={setPeoplePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid="people-picker">
                        <Users className="h-4 w-4 mr-2" />
                        {selectedPeopleIds.length > 0
                          ? `${selectedPeopleIds.length} ${selectedPeopleIds.length === 1 ? "person" : "people"} selected`
                          : "Select people…"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search people…" />
                        <CommandList>
                          <CommandEmpty>No people found.</CommandEmpty>
                          <CommandGroup>
                            {selectableUsers.map((u) => {
                              const selected = form.allowed_user_ids.includes(u.id);
                              return (
                                <CommandItem
                                  key={u.id}
                                  value={`${u.name} ${u.email}`}
                                  onSelect={() => toggleUser(u.id)}
                                >
                                  <Check className={`h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`} />
                                  <div className="flex flex-col">
                                    <span className="text-sm">{u.name}</span>
                                    <span className="text-xs text-slate-500">{u.email}{u.department ? ` · ${u.department}` : ""}</span>
                                  </div>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {selectedPeopleIds.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedPeopleIds.map((id) => {
                          const u = selectableUsers.find((x) => x.id === id);
                          return (
                            <Badge key={id} variant="secondary" className="gap-1 pr-1">
                              {u ? u.name : id}
                              <button
                                type="button"
                                onClick={() => toggleUser(id)}
                                className="rounded-full hover:bg-slate-300/60 p-0.5"
                                aria-label={`Remove ${u ? u.name : id}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <div className="font-semibold">{needsApproval ? "Approval required" : "Pro tip"}</div>
                <p>
                  {needsApproval
                    ? `Admin Approval required : Bounty exceeds department limit (${maxPerTicket}c). Your department admin will review before this ticket is published and budget is deducted.`
                    : "The clearer your description, the faster you'll get matched. Mention the company, role, timeline."}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="new-cancel">Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="new-submit" className="bg-blue-600 hover:bg-blue-700">
                {loading
                  ? (needsApproval ? "Submitting…" : "Publishing…")
                  : (needsApproval ? "Request approval" : "Publish request")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
